import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { useActiveSection } from "../hooks/useReveal";
import { Logo } from "./Logo";

// Section-scroll link — routes to /#hash then smooth-scrolls (App.js ScrollHandler)
const SectionLink = ({ hash, children, active, testid, onClick }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) onClick();
    // If already on home, scroll manually (avoids no-op when hash unchanged)
    if (location.pathname === "/") {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // update URL hash without triggering re-render
        window.history.replaceState(null, "", `/#${hash}`);
        return;
      }
    }
    navigate(`/#${hash}`);
  };

  return (
    <a
      href={`/#${hash}`}
      onClick={handleClick}
      data-testid={testid}
      className={`relative text-xs tracking-[0.25em] uppercase font-bold transition-colors ${
        active ? "text-yellow-400" : "text-white/90 hover:text-yellow-400"
      }`}
    >
      {children}
      <span
        aria-hidden
        className={`absolute -bottom-1.5 left-0 h-[2px] bg-yellow-400 transition-all duration-500 ${
          active ? "w-full" : "w-0 group-hover:w-full"
        }`}
      />
    </a>
  );
};

const RouteLink = ({ to, children, active, testid, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testid}
    className={`relative text-xs tracking-[0.25em] uppercase font-bold transition-colors ${
      active ? "text-yellow-400" : "text-white/90 hover:text-yellow-400"
    }`}
  >
    {children}
    <span
      aria-hidden
      className={`absolute -bottom-1.5 left-0 h-[2px] bg-yellow-400 transition-all duration-500 ${
        active ? "w-full" : "w-0"
      }`}
    />
  </Link>
);

export const Header = () => {
  const { lang, toggleLang, t } = useLanguage();
  const { setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Watch section visibility only on home page
  const activeSection = useActiveSection(
    location.pathname === "/" ? ["cabo", "mindset"] : [],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location.pathname === "/";
  const homeActive = isHome && !activeSection;
  const caboActive = isHome && activeSection === "cabo";
  const mindsetActive = isHome && activeSection === "mindset";
  const shopActive = location.pathname === "/shop" || location.pathname.startsWith("/product");
  const aboutActive = location.pathname === "/about";
  const contactActive = location.pathname === "/contact";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-black/60 to-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center group flex-shrink-0">
          <Logo size="md" />
          <span className="sr-only">Cabo Unstoppable</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
          <RouteLink to="/" active={homeActive} testid="nav-home">
            {t.nav.home}
          </RouteLink>
          <RouteLink to="/shop" active={shopActive} testid="nav-shop">
            {t.nav.shop}
          </RouteLink>
          <SectionLink hash="cabo" active={caboActive} testid="nav-cabo">
            {t.nav.cabo}
          </SectionLink>
          <SectionLink hash="mindset" active={mindsetActive} testid="nav-mindset">
            {t.nav.mindset}
          </SectionLink>
          <RouteLink to="/about" active={aboutActive} testid="nav-about">
            {t.nav.about}
          </RouteLink>
          <RouteLink to="/contact" active={contactActive} testid="nav-contact">
            {t.nav.contact}
          </RouteLink>
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          <button
            onClick={toggleLang}
            data-testid="language-toggle"
            aria-label="Toggle language"
            className="flex items-center gap-1 text-[11px] sm:text-xs font-bold tracking-widest uppercase border border-white/30 px-2.5 py-1 sm:px-3 sm:py-1.5 hover:border-yellow-400 hover:text-yellow-400 transition-colors"
          >
            <span className={lang === "en" ? "text-yellow-400" : "text-white/60"}>EN</span>
            <span className="text-white/40">/</span>
            <span className={lang === "es" ? "text-yellow-400" : "text-white/60"}>ES</span>
          </button>

          <button
            onClick={() => setIsOpen(true)}
            data-testid="open-cart-button"
            className="relative p-2 hover:text-yellow-400 transition-colors flex items-center gap-2"
            aria-label={t.comingSoon.drawerTitle}
            title={t.comingSoon.drawerTitle}
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.8} />
            <span className="hidden md:inline text-[10px] tracking-[0.25em] uppercase font-bold text-yellow-400 border border-yellow-400/40 px-2 py-0.5">
              {t.comingSoon.badge}
            </span>
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-button"
            className="lg:hidden p-2"
            aria-label={t.nav.menu}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-nav-panel"
          className="lg:hidden bg-black border-t border-white/10"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-home"
              className={`text-2xl font-display uppercase tracking-tight ${
                homeActive ? "text-yellow-400" : "text-white"
              }`}
            >
              {t.nav.home}
            </Link>
            <Link
              to="/shop"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-shop"
              className={`text-2xl font-display uppercase tracking-tight ${
                shopActive ? "text-yellow-400" : "text-white"
              }`}
            >
              {t.nav.shop}
            </Link>
            <SectionLink
              hash="cabo"
              active={caboActive}
              testid="mobile-nav-cabo"
              onClick={() => setMobileOpen(false)}
            >
              <span className="text-2xl font-display uppercase tracking-tight">
                {t.nav.cabo}
              </span>
            </SectionLink>
            <SectionLink
              hash="mindset"
              active={mindsetActive}
              testid="mobile-nav-mindset"
              onClick={() => setMobileOpen(false)}
            >
              <span className="text-2xl font-display uppercase tracking-tight">
                {t.nav.mindset}
              </span>
            </SectionLink>
            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-about"
              className={`text-2xl font-display uppercase tracking-tight ${
                aboutActive ? "text-yellow-400" : "text-white"
              }`}
            >
              {t.nav.about}
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-contact"
              className={`text-2xl font-display uppercase tracking-tight ${
                contactActive ? "text-yellow-400" : "text-white"
              }`}
            >
              {t.nav.contact}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
